/**
 * SystemMetrics - Permanent right-side panel showing roof and module metrics
 * Always visible in both Site and System modes
 *
 * Shows GeoTIFF viewer measurements as primary values
 * Optionally shows Google Solar API estimates as expandable comparison
 */

import React, { useState } from 'react';
import { Home, AlertTriangle, Grid, Zap, ChevronDown, ChevronUp } from 'lucide-react';
import { AuroraCard, AuroraCardSection } from './AuroraCard';

interface SystemMetricsProps {
  // Roof data (our measurements from GeoTIFF viewer)
  roofArea?: number; // m²
  obstructionArea?: number; // m²
  netArea?: number; // m²

  // Module data (our measurements from GeoTIFF viewer)
  moduleCount?: number;
  totalModuleArea?: number; // m²
  roofCoverage?: number; // percentage
  systemCapacity?: number; // kW
  modulePower?: number; // watts per module
  moduleModel?: string; // Module model name
  moduleBrand?: string; // Module brand/manufacturer
  moduleAzimuth?: number; // Module/roof azimuth in degrees (0-360)

  // Free-field modules (not on roofs)
  freeFieldModuleCount?: number;
  freeFieldSegmentArea?: number; // m² - area of free-field segments

  // Google Solar API comparison data (optional)
  googleComparison?: {
    roofArea?: number;
    moduleCount?: number;
    systemCapacity?: number;
    modulePower?: number;
  };
}

export const SystemMetrics: React.FC<SystemMetricsProps> = ({
  roofArea,
  obstructionArea = 0,
  netArea,
  moduleCount = 0,
  totalModuleArea: totalModuleAreaProp = 0,
  roofCoverage: roofCoverageProp = 0,
  systemCapacity: systemCapacityProp = 0,
  modulePower = 0,
  moduleModel,
  moduleBrand,
  moduleAzimuth,
  freeFieldModuleCount,
  freeFieldSegmentArea,
  googleComparison,
}) => {
  // Guard against NaN values
  const totalModuleArea = isNaN(totalModuleAreaProp) ? 0 : totalModuleAreaProp;
  const roofCoverage = isNaN(roofCoverageProp) ? 0 : roofCoverageProp;
  const systemCapacity = isNaN(systemCapacityProp) ? 0 : systemCapacityProp;
  const [showGoogleComparison, setShowGoogleComparison] = useState(false);
  return (
    <AuroraCard
      title=""
      collapsed={false}
    >
      <AuroraCardSection>
        {/* Roof Data Section */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-gray-200">
            <Home className="h-4 w-4 text-gray-700" />
            <span className="text-sm font-bold text-gray-700">Roof Data</span>
          </div>

          <div className="space-y-2 text-xs">
            {roofArea !== undefined ? (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Gross Area:</span>
                  <span className="font-semibold text-black">{roofArea.toFixed(2)} m²</span>
                </div>

                {freeFieldSegmentArea !== undefined && freeFieldSegmentArea > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span className="flex items-center gap-1">
                      <span className="text-xs">🌱</span>
                      Field Segments:
                    </span>
                    <span className="font-semibold">{freeFieldSegmentArea.toFixed(2)} m²</span>
                  </div>
                )}

                {obstructionArea > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Obstructions:
                    </span>
                    <span className="font-semibold">-{obstructionArea.toFixed(2)} m²</span>
                  </div>
                )}

                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="text-green-700 font-semibold">Net Usable:</span>
                  <span className="font-bold text-green-700">
                    {(netArea || (roofArea - obstructionArea)).toFixed(2)} m²
                  </span>
                </div>
              </>
            ) : freeFieldSegmentArea !== undefined && freeFieldSegmentArea > 0 ? (
              <>
                <div className="flex justify-between text-green-600">
                  <span className="flex items-center gap-1">
                    <span className="text-xs">🌱</span>
                    Field Segments:
                  </span>
                  <span className="font-semibold">{freeFieldSegmentArea.toFixed(2)} m²</span>
                </div>

                {obstructionArea > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Obstructions:
                    </span>
                    <span className="font-semibold">-{obstructionArea.toFixed(2)} m²</span>
                  </div>
                )}

                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="text-green-700 font-semibold">Net Usable:</span>
                  <span className="font-bold text-green-700">
                    {(freeFieldSegmentArea - obstructionArea).toFixed(2)} m²
                  </span>
                </div>
              </>
            ) : (
              <div className="text-gray-500 italic">Select a roof polygon or draw field segments</div>
            )}
          </div>
        </div>

        {/* Module Data Section */}
        <div>
          <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-gray-200">
            <Grid className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-bold text-blue-700">Module Data</span>
          </div>

          <div className="space-y-2 text-xs">
            {moduleCount > 0 ? (
              <>
                {/* Module Brand and Model */}
                {moduleBrand && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Brand:</span>
                    <span className="font-semibold text-black">{moduleBrand}</span>
                  </div>
                )}

                {moduleModel && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Model:</span>
                    <span className="font-semibold text-black">{moduleModel}</span>
                  </div>
                )}

                {modulePower > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Module Power:</span>
                    <span className="font-semibold text-black">{modulePower} W</span>
                  </div>
                )}

                {moduleAzimuth !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Azimuth:</span>
                    <span className="font-semibold text-black">{moduleAzimuth.toFixed(1)}°</span>
                  </div>
                )}

                {(moduleBrand || moduleModel || modulePower > 0 || moduleAzimuth !== undefined) && (
                  <div className="border-t border-gray-200 my-2"></div>
                )}

                <div className="flex justify-between">
                  <span className="text-gray-600">Module Count:</span>
                  <span className="font-semibold text-black">{moduleCount}</span>
                </div>

                {freeFieldModuleCount !== undefined && freeFieldModuleCount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span className="flex items-center gap-1">
                      <span className="text-xs">🌱</span>
                      Free-Field:
                    </span>
                    <span className="font-semibold">{freeFieldModuleCount} modules</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span className="text-gray-600">Module Area:</span>
                  <span className="font-semibold text-black">{totalModuleArea.toFixed(2)} m²</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Roof Coverage:</span>
                  <span className="font-semibold text-black">{roofCoverage.toFixed(1)}%</span>
                </div>

                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="text-blue-700 font-semibold flex items-center gap-1">
                    <Zap className="h-3 w-3" />
                    System Capacity:
                  </span>
                  <span className="font-bold text-blue-700">{systemCapacity.toFixed(2)} kW</span>
                </div>
              </>
            ) : (
              <div className="text-gray-500 italic">No modules placed</div>
            )}
          </div>
        </div>

        {/* Google Solar API Comparison - Expandable */}
        {googleComparison && (
          <div className="mt-4 pt-4 border-t-2 border-gray-200">
            <button
              onClick={() => setShowGoogleComparison(!showGoogleComparison)}
              className="w-full flex items-center justify-between text-xs font-semibold text-gray-600 hover:text-gray-800 transition-colors"
            >
              <span className="flex items-center gap-1">
                <span className="text-yellow-600">⚡</span>
                Google Solar API Estimate
              </span>
              {showGoogleComparison ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>

            {showGoogleComparison && (
              <div className="mt-3 space-y-2 text-xs bg-yellow-50 p-3 rounded border border-yellow-200">
                <div className="text-xs text-yellow-700 font-semibold mb-2">
                  Reference values from Google Solar API
                </div>

                {googleComparison.roofArea !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Roof Area:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-black">
                        {googleComparison.roofArea.toFixed(2)} m²
                      </span>
                      {roofArea !== undefined && (
                        <span className="text-xs text-gray-500">
                          ({roofArea > googleComparison.roofArea ? '+' : ''}
                          {((roofArea - googleComparison.roofArea) / googleComparison.roofArea * 100).toFixed(0)}%)
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {googleComparison.moduleCount !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Module Count:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-black">
                        {googleComparison.moduleCount}
                      </span>
                      {moduleCount > 0 && (
                        <span className="text-xs text-gray-500">
                          ({moduleCount > googleComparison.moduleCount ? '+' : ''}
                          {moduleCount - googleComparison.moduleCount})
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {googleComparison.modulePower !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Module Power:</span>
                    <span className="font-semibold text-black">
                      {googleComparison.modulePower} W
                    </span>
                  </div>
                )}

                {googleComparison.systemCapacity !== undefined && (
                  <div className="flex justify-between pt-2 border-t border-yellow-300">
                    <span className="text-yellow-700 font-semibold">System Capacity:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-yellow-700">
                        {googleComparison.systemCapacity.toFixed(2)} kW
                      </span>
                      {systemCapacity > 0 && (
                        <span className="text-xs text-gray-500">
                          ({systemCapacity > googleComparison.systemCapacity ? '+' : ''}
                          {((systemCapacity - googleComparison.systemCapacity) / googleComparison.systemCapacity * 100).toFixed(0)}%)
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </AuroraCardSection>
    </AuroraCard>
  );
};
